// PaymentMethodManager component for AgenticRCM
import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Trash2, Plus, CreditCard } from 'lucide-react';

// Get API base URL for SaaSFoundation
const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    const isProduction = window.location.hostname !== 'localhost' || 
                        (window.location.port !== '3000' && window.location.port !== '');
    if (isProduction) {
      return '/api/v1'; // nginx proxies /api to SaaSFoundation:8001
    }
  }
  
  if (typeof window !== 'undefined' && window.location) {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return (() => { const __h = hostname; return (__h && __h !== 'localhost' && __h !== '127.0.0.1' && !/^(\d{1,3}\.){3}\d{1,3}$/.test(__h)) ? '/api/v1' : `${protocol}//${hostname}:8001/api/v1`; })();
  }
  
  return 'http://localhost:8001/api/v1';
};

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const PaymentMethodManager = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingMethod, setAddingMethod] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/payment-methods`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentMethods(data.data || data || []);
      } else {
        setError('Failed to load payment methods');
      }
    } catch (err) {
      setError('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMethod = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setAddingMethod(true);
    setError(null);

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (error) {
        throw new Error(error.message);
      }

      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/payment-methods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'card',
          stripe_token: paymentMethod.id,
          is_default: paymentMethods.length === 0
        })
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Failed to save payment method');
      }

      setSuccess('Payment method added successfully');
      setAddingMethod(false);
      loadPaymentMethods();
      elements.getElement(CardElement).clear();
      
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    } finally {
      setAddingMethod(false);
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId) => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/payment-methods/${paymentMethodId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccess('Payment method deleted successfully');
        loadPaymentMethods();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError('Failed to delete payment method');
        setTimeout(() => setError(null), 5000);
      }
    } catch (err) {
      setError('Failed to delete payment method');
      setTimeout(() => setError(null), 5000);
    }
  };

  const getCardBrandIcon = (brand) => {
    return '💳';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading payment methods...</p>
        </div>
      </div>
    );
  }

  // Subscription / billing pages use CardElement; requires <Elements> from App. On non-HTTPS (non-localhost)
  // Stripe is not loaded unless REACT_APP_STRIPE_ALLOW_HTTP=true — hooks are null and the form cannot work.
  if (!stripe || !elements) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <CreditCard className="w-5 h-5 mr-2 text-gray-700" />
          <h3 className="text-lg font-semibold text-gray-900">Payment Methods</h3>
        </div>
        <p className="text-sm text-gray-600">
          Card entry is unavailable because Stripe.js is not loaded. Use <strong>HTTPS</strong>, open the app on{' '}
          <strong>localhost</strong> for HTTP dev, or set{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">REACT_APP_STRIPE_ALLOW_HTTP=true</code> and rebuild.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-6">
          <CreditCard className="w-5 h-5 mr-2 text-gray-700" />
          <h3 className="text-lg font-semibold text-gray-900">Payment Methods</h3>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-600 text-sm">{success}</p>
          </div>
        )}

        <div className="space-y-3 mb-6">
          {paymentMethods.map((method) => (
            <div
              key={method.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
            >
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{getCardBrandIcon(method.brand)}</span>
                <div>
                  <p className="font-medium">
                    {method.brand?.charAt(0).toUpperCase() + method.brand?.slice(1) || 'Card'} •••• {method.last_four}
                  </p>
                  <p className="text-sm text-gray-600">
                    Expires {method.exp_month}/{method.exp_year}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {method.is_default && (
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    Default
                  </span>
                )}
                <button
                  onClick={() => handleDeletePaymentMethod(method.id)}
                  disabled={method.is_default && paymentMethods.length === 1}
                  className="p-2 text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t pt-6">
          <h4 className="text-lg font-medium mb-4">Add New Payment Method</h4>
          <form onSubmit={handleAddPaymentMethod}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Card Details
              </label>
              <div className="border border-gray-300 rounded-md p-3">
                <CardElement options={CARD_ELEMENT_OPTIONS} />
              </div>
            </div>

            <button
              type="submit"
              disabled={!stripe || addingMethod}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4 mr-2" />
              {addingMethod ? 'Adding...' : 'Add Payment Method'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodManager;

